import { Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useUsers } from "@/hooks/use-users";

const ENGINEER_ROLES = new Set(["engineer", "new_engineer"]);

export interface ResourceOption {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ResourcePickerProps {
  selected: ResourceOption[];
  onChange: (next: ResourceOption[]) => void;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function ResourcePicker({ selected, onChange }: ResourcePickerProps) {
  const [search, setSearch] = useState("");
  const { data: users = [] } = useUsers({ search: undefined });

  const engineers = useMemo(
    () => users.filter((u) => ENGINEER_ROLES.has(u.role) && !u.banned),
    [users],
  );

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return engineers
      .filter((u) => !selectedIds.has(u.id))
      .filter((u) => {
        if (!q) return true;
        return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      })
      .slice(0, 12);
  }, [engineers, selectedIds, search]);

  function add(u: ResourceOption) {
    onChange([...selected, u]);
    setSearch("");
  }

  function remove(id: string) {
    onChange(selected.filter((s) => s.id !== id));
  }

  return (
    <div className="resource-picker">
      <div className="resource-picker__chips">
        {selected.length === 0 && (
          <span className="t-muted t-small">No engineers assigned yet.</span>
        )}
        {selected.map((u) => (
          <span key={u.id} className="resource-chip">
            <span className="resource-chip__avatar">{initials(u.name)}</span>
            <span className="resource-chip__name">{u.name}</span>
            <button
              type="button"
              className="resource-chip__remove"
              onClick={() => remove(u.id)}
              aria-label={`Remove ${u.name}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>

      <div className="search-input" style={{ marginTop: 10 }}>
        <Search size={14} />
        <input
          placeholder="Search engineers by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="resource-picker__list">
        {filtered.length === 0 ? (
          <div className="t-muted t-small" style={{ padding: "10px 4px" }}>
            {engineers.length === 0 ? "No engineers available." : "No matching engineers."}
          </div>
        ) : (
          filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              className="resource-picker__item"
              onClick={() => add({ id: u.id, name: u.name, email: u.email, role: u.role })}
            >
              <span className="resource-chip__avatar">{initials(u.name)}</span>
              <span className="resource-picker__item-text">
                <span className="resource-picker__item-name">{u.name}</span>
                <span className="resource-picker__item-email">{u.email}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
